const DEFAULT_ALGORITHM = {
  name: 'RSASSA-PKCS1-v1_5',
  modulusLength: 2048,
  publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
  hash: 'SHA-256'
}

export class KeyPair {
  #algorithm

  /** @type {CryptoKeyPair | undefined} */
  #keypair

  constructor(algorithm = DEFAULT_ALGORITHM) {
    this.#algorithm = algorithm
    if (!crypto.subtle) {
      throw 'Crypto.subtle requires secure environment'
    }
  }

  async generate() {
    this.#keypair = await crypto.subtle.generateKey(
      this.#algorithm,
      true,
      ['sign', 'verify']
    )
  }

  async publicJwk() {
    if (!this.#keypair) {
      throw `Must generate keypair`
    }
    const publicKey = this.#keypair.publicKey
    const publicJwk = await crypto.subtle.exportKey('jwk', publicKey)
    return publicJwk
  }

  async privateJwk() {
    if (!this.#keypair) {
      throw `Must generate keypair`
    }
    const privateKey = this.#keypair.privateKey
    const privateJwk = await crypto.subtle.exportKey('jwk', privateKey)
    return privateJwk
  }

  async publicPem() {
    if (!this.#keypair) {
      throw `Must generate keypair`
    }
    const publicKey = this.#keypair.publicKey
    const spkiKey = await crypto.subtle.exportKey('spki', publicKey)
    const base64Key = btoa(String.fromCharCode(...new Uint8Array(spkiKey)))
    const base64KeyLines = base64Key.match(/.{1,64}/g)?.join('\n') ?? ''
    const publicPem = `-----BEGIN PUBLIC KEY-----\n${base64KeyLines}\n-----END PUBLIC KEY-----`
    return publicPem
  }

  async privatePem() {
    if (!this.#keypair) {
      throw `Must generate keypair`
    }
    const privateKey = this.#keypair.privateKey
    const pkcs8Key = await crypto.subtle.exportKey('pkcs8', privateKey)
    const base64Key = btoa(String.fromCharCode(...new Uint8Array(pkcs8Key)))
    const base64KeyLines = base64Key.match(/.{1,64}/g)?.join('\n') ?? ''
    const privatePem = `-----BEGIN PRIVATE KEY-----\n${base64KeyLines}\n-----END PRIVATE KEY-----`
    return privatePem
  }
}
